import {TFunction} from "react-i18next";
import {DateObservable} from "../../../observables/dateObservable";
import {TextObservable} from "../../../observables/textObservable";
import {BooleanObservable} from "../../../observables/booleanObservable";
import {MultiVillageSelectObservableList} from "../../../observables/multiVillageSelectObservableList";
import {format} from "date-fns";
import {AuthUser} from "../../../../utils/auth/user";
import {KeyValue} from "../../../observables/keyValue";
import {
  allVillageKeyValue,
  allVillagesInAU,
  allVillagesInNZ
} from "../../../../shared/constants";

export default class Occasion {
  /**
   * The unique identifier of the Occasion/Special Day.
   */
  id: string;
  /**
   * The date of the Occasion/Special Day.
   */
  date: DateObservable;
  /**
   * Title of the Occasion/Special Day.
   */
  title: TextObservable;
  /**
   * Indicate if the drink is provided for a special day
   */
  hasDrink: BooleanObservable;
  /**
   * Indicate if the food is provided for a special day
   */
  hasFood: BooleanObservable;
  /**
   * Is this Occasion/Special Day marked as the primary.
   */
  isPrimary: BooleanObservable;
  /**
   * Village ID which the Occasion/Special Day is visible to.
   */
  villageIds: MultiVillageSelectObservableList;

  constructor(
    id?: string,
    date?: string,
    title?: string,
    hasDrink?: boolean,
    hasFood?: boolean,
    isPrimary?: boolean,
    villageIds?: string[] | null
  ) {
    this.id = id ?? "";
    this.date = new DateObservable(date ? new Date(date) : null);
    this.title = new TextObservable(
      title ?? "",
      250,
      (value) => value?.trim() !== ""
    );
    this.hasDrink = new BooleanObservable(hasDrink ?? false);
    this.hasFood = new BooleanObservable(hasFood ?? false);
    this.isPrimary = new BooleanObservable(isPrimary ?? false);
    this.villageIds = this.setVillageIds(villageIds ?? []);
  }

  get canPublish() {
    return this.date.isValid && this.title.isValid && this.villageIds.isValid;
  }

  get hasChanges() {
    return (
      this.date.isDirty ||
      this.title.isDirty ||
      this.hasDrink.isDirty ||
      this.hasFood.isDirty ||
      this.isPrimary.isDirty ||
      this.villageIds.isDirty
    );
  }

  setIsDirty = (value: boolean) => {
    this.date.setIsDirty(value);
    this.title.setIsDirty(value);
    this.hasDrink.setIsDirty(value);
    this.hasFood.setIsDirty(value);
    this.isPrimary.setIsDirty(value);
    this.villageIds.setIsDirty(value);
  };

  getCateringLabel(translationFunction: TFunction<string[]>) {
    if (this.hasDrink.value && this.hasFood.value) {
      return translationFunction("translation:labels.foodAndBeverage");
    } else if (this.hasDrink.value) {
      return translationFunction("translation:labels.beverage");
    } else if (this.hasFood.value) {
      return translationFunction("translation:labels.food");
    }

    return translationFunction("translation:labels.none");
  }

  setVillageIds(villageIds: string[]) {
    const multivillages =
      AuthUser.getInstance().Villages?.map(
        (village) => new KeyValue(village.id, village.name)
      ) ?? [];
    const selectedVillageIds = multivillages.filter((village) =>
      villageIds.includes(village.value)
    );

    multivillages.unshift(allVillagesInNZ);
    multivillages.unshift(allVillagesInAU);
    multivillages.unshift(allVillageKeyValue);

    return new MultiVillageSelectObservableList(
      multivillages,
      selectedVillageIds,
      1
    );
  }

  toJson() {
    return {
      date: format(this.date.date!, "yyyy-MM-dd"),
      title: this.title.text,
      hasFood: this.hasFood.value,
      hasDrink: this.hasDrink.value,
      isPrimary: this.isPrimary.value,
      villages: this.villageIds.selectedItems.map((village) => village.value)
    };
  }

  copyOccasion() {
    return new Occasion(
      this.id,
      format(this.date.date!, "yyyy-MM-dd"),
      this.title.text,
      this.hasDrink.value,
      this.hasFood.value,
      this.isPrimary.value,
      this.villageIds.selectedItems.map((village) => village.value)
    );
  }
}
